// DS2 FormField: a label, the field, then the footer under it - a helper text, a warning or an error ({ type, message }).
// children: the field, or a function that gets the props to wire it to the label and the error:
//   <FormField label="Name" footer={{ type: "error", message }}>{({ labelId, isInvalid, errorId }) => <Input id={labelId}
//   isInvalid={isInvalid} aria-describedby={errorId} />}</FormField>
// required: a red * after the label, also passed to the children. showHelp: a "?" after the label, tinted by its type,
// its message on hover and for screen readers. FormField.ErrorMessage: react-hook-form's FieldError as a message - its
// own, else a localized text by its type (required, min, max, pattern, validate).
import cx from "classnames";
import { useId, type ReactNode } from "react";
import type { FieldError } from "react-hook-form";
import styles from "./FormField.module.css";
import useI18n from "../../hooks/useTranslations";

export type FormFieldChildrenProps = {
  labelId: string;
  required: boolean;
  disabled: boolean;
  isInvalid: boolean;
  // id of the error message, when present - wire it to the field via aria-describedby
  errorId: string | undefined;
};

export type FormFieldProps = {
  label?: string;
  children: ReactNode | ((props: FormFieldChildrenProps) => ReactNode);
  required?: boolean;
  disabled?: boolean;
  footer?: {
    type: "info" | "warning" | "error";
    message: string;
  };
  hidden?: boolean;
  // a "?" after the label
  showHelp?: {
    type: "info" | "warning" | "error";
    message: string;
  };
  className?: string;
};

const HELP_CLASS = { info: styles.helpInfo, warning: styles.helpWarning, error: styles.helpError };
const FOOTER_CLASS = { info: styles.hint, warning: styles.warning, error: styles.error };

export function FormField({
  label,
  children,
  required = false,
  disabled = false,
  footer,
  hidden,
  showHelp,
  className,
}: FormFieldProps) {
  const labelId = useId();
  const footerId = useId();
  const isInvalid = footer?.type === "error";
  const errorId = isInvalid ? footerId : undefined;
  return (
    <div className={cx(styles.field, disabled && styles.disabled, hidden && styles.hidden, className)}>
      {label ? (
        <div className={styles.labelRow}>
          <label htmlFor={labelId} className={styles.label}>
            {label}
            {required ? <span className={styles.required}> *</span> : null}
          </label>
          {showHelp ? (
            <span
              className={cx(styles.help, HELP_CLASS[showHelp.type])}
              role="img"
              aria-label={showHelp.message}
              title={showHelp.message}
            >
              ?
            </span>
          ) : null}
        </div>
      ) : null}
      {typeof children === "function" ? children({ labelId, required, disabled, isInvalid, errorId }) : children}
      {footer ? (
        <div id={footerId} className={FOOTER_CLASS[footer.type]}>
          {footer.message}
        </div>
      ) : null}
    </div>
  );
}

type ErrorMessageProps = Pick<FieldError, "message" | "type"> & { id?: string };

const ERROR_TEXT = {
  required: "requiredFieldLabel",
  min: "fieldTooSmallLabel",
  max: "fieldTooLargeLabel",
  pattern: "fieldInvalidFormatLabel",
  validate: "fieldInvalidValueLabel",
} as const;

function ErrorMessage({ id, message, type }: ErrorMessageProps) {
  const t = useI18n().translation.designSystem;
  const key = ERROR_TEXT[type as keyof typeof ERROR_TEXT];
  return (
    <div id={id} className={styles.error}>
      {message || (key ? t[key] : "")}
    </div>
  );
}

FormField.ErrorMessage = ErrorMessage;
